import { getComponentRadius } from 'features/assetModeling'
import {
  GeckoComponentModel,
  GeckoComponentModelType,
  GeckoMeasurementUnit,
  GeckoPressureVesselShell,
  PressureVesselShellOrientation,
} from 'types'

import { pipingMaterialFamily } from './pipingMaterialFamily'
import { PipingMaterialGroup } from './pipingMaterialGroups'

/**
 * API 579-1/ASME FFS-1 (2021) Part 4 §4.2.5 / Table 4.2 component-type
 * classification for a single assessment REGION (an LTA), plus the typed facts
 * that drive it.
 *
 * This module is the single source of truth for the classification RULES. They
 * live in code (an explicit ordered function), not a data-driven tree, so the
 * precedence is type-checked and directly unit-testable. The API rules are a
 * fixed published standard, so they are versioned here rather than in Rolodex.
 *
 * Nothing here is React-aware, and nothing here is user-facing wording: the
 * prompts/options metadata lives in `ffsClassificationUi.ts` and the cited help
 * text in `ffsClassificationContent.ts`, both of which depend on this module and
 * never the other way round. Wording changes therefore cannot touch the logic.
 */

export const COMPONENT_TYPES = ['A', 'B1', 'B2', 'C'] as const
export type ComponentType = (typeof COMPONENT_TYPES)[number]

/**
 * Where the assessment region sits — the highest-precedence discriminator.
 *
 * All fourteen are classified here, but the form currently OFFERS only
 * `remote-shell-body` (see `SUPPORTED_REGION_KINDS` in `ffsClassificationUi.ts`
 * for why). The rest are retained-but-not-offered: still routed, still
 * persisted, still parsed off saved records — just not selectable today.
 */
export const REGION_KINDS = [
  'remote-shell-body',
  'nozzle',
  'branch',
  'flange',
  'tubesheet',
  'cone-reinforcement',
  'cyl-to-flat-head-jct',
  'head-to-shell-jct',
  'skirt',
  'lug',
  'support-attach',
  'tank-shell-to-bottom-jct',
  'stiffening-ring-attach',
  'other',
] as const
export type RegionKind = (typeof REGION_KINDS)[number]

export const SHELL_GEOMETRIES = [
  'horizontal',
  'vertical',
  'sphere',
  'piping',
  'other-code-designed-body',
] as const
export type ShellGeometry = (typeof SHELL_GEOMETRIES)[number]

/**
 * The typed facts the engineer supplies (some prefilled from the asset model).
 * Every field is optional: the classifier reports which are still `missing`
 * rather than assuming a default. Dimensional facts are in METERS (the UI
 * resolves units before storing).
 */
export interface ClassificationFacts {
  region?: RegionKind
  // 2:1-elliptical head-to-shell-junction exception (Table 4.2):
  headIs2to1Elliptical?: boolean
  onCylinderSide?: boolean
  pressureOnlyLoading?: boolean
  // Stiffening-ring attachment (Fig 4.18): ratio > 0.38 => Type C.
  stiffenerRatio?: number
  shellGeometry?: ShellGeometry
  // Dimensional limits (meters). D is the API diameter (see the outer-diameter
  // caveat in `extractClassificationFacts`).
  Lss?: number
  H?: number
  D?: number
  // Piping (Fig 4.5): NPS in inches, design temperature in °C.
  materialGroup?: PipingMaterialGroup
  nps?: number
  designTempC?: number
  // Type-A loading gates:
  pressureGovernsThickness?: boolean
  supplementalLoadsGovern?: boolean
  // Generic fallback (unlisted region):
  hasCodeDesignEquation?: boolean
  codeAssessmentProcedureAvailable?: boolean
}

export type ClassificationResult =
  | { status: 'resolved'; type: ComponentType; trace: ReadonlyArray<string> }
  | { status: 'more-data'; missing: ReadonlyArray<string> }

// Dimensional limits (API 579-1 Part 4, Fig 4.3 / 4.4), in meters.
export const HORIZONTAL_LSS_OVER_D_MAX = 2.5
export const HORIZONTAL_D_MAX_M = 3.05 // 10 ft
export const VERTICAL_H_OVER_D_MAX = 3.0
export const VERTICAL_H_MAX_M = 30.5 // 100 ft
export const STIFFENER_RATIO_MAX = 0.38 // Fig 4.18

const resolved = (
  type: ComponentType,
  trace: ReadonlyArray<string>
): ClassificationResult => ({ status: 'resolved', type, trace })
const moreData = (...missing: string[]): ClassificationResult => ({
  status: 'more-data',
  missing,
})

/** True when a fact is a usable, physically-valid positive number. */
function isPositiveFinite(n: number | undefined): n is number {
  return n !== undefined && Number.isFinite(n) && n > 0
}

/** The subset of numeric fact keys that are missing/invalid (for `moreData`). */
function missingNumericKeys(
  f: ClassificationFacts,
  keys: ReadonlyArray<'Lss' | 'H' | 'D'>
): string[] {
  return keys.filter((k) => !isPositiveFinite(f[k]))
}

// --- Fig 4.5 piping bands (Type-A eligibility for piping) -------------------
// The temperature-vs-NPS shaded regions of API 579-1/ASME FFS-1 (2021) Fig 4.5
// (imperial) / Fig 4.5M (metric): a straight pipe / elbow / bend with NO
// structural attachment whose design temperature + NPS fall inside the region
// may be treated as Type A. Sizes are NPS INCHES (from Fig 4.5); temperatures
// are °C (from the metric Fig 4.5M) so they match `designTempC` exactly.
export interface Fig45Band {
  materialGroup: PipingMaterialGroup
  /** NPS range in inches, inclusive. */
  npsMinIn: number
  npsMaxIn: number
  /** Design-temperature floor + ceiling in °C, inclusive. */
  designTempMinC: number
  designTempMaxC: number
}

// Boundary note: bands share NPS endpoints (4, 8), so at an exact boundary size
// the `.some` over inclusive ranges lets the higher-temperature plateau win
// (e.g. NPS 4 → 204 °C ceiling). This is the plateau-inclusive reading; the
// classification is engineer-confirmed, so it's a suggestion, not a gate.
export const FIG_4_5_BANDS: ReadonlyArray<Fig45Band> = [
  // (a) Carbon & Low Alloy Steels — floor -46 °C, NPS 2–16".
  {
    materialGroup: 'carbon-low-alloy',
    npsMinIn: 2,
    npsMaxIn: 4,
    designTempMinC: -46,
    designTempMaxC: 204,
  },
  {
    materialGroup: 'carbon-low-alloy',
    npsMinIn: 4,
    npsMaxIn: 8,
    designTempMinC: -46,
    designTempMaxC: 149,
  },
  {
    materialGroup: 'carbon-low-alloy',
    npsMinIn: 8,
    npsMaxIn: 16,
    designTempMinC: -46,
    designTempMaxC: 93,
  },
  // (b) High Alloy & Nonferrous Steels — ceiling 93 °C, NPS 2–10".
  {
    materialGroup: 'high-alloy-nonferrous',
    npsMinIn: 2,
    npsMaxIn: 4,
    designTempMinC: -101,
    designTempMaxC: 93,
  },
  {
    materialGroup: 'high-alloy-nonferrous',
    npsMinIn: 4,
    npsMaxIn: 10,
    designTempMinC: -46,
    designTempMaxC: 93,
  },
]

/**
 * Whether the piping facts fall within a Type-A band. `null` = a required fact
 * (material group / NPS / design temp) is missing → "cannot confirm" (the
 * classifier then asks for it). A present-but-out-of-region input → `false`
 * (→ B1). NPS in inches, temperature in °C.
 */
export function isWithinFig45Bands(f: ClassificationFacts): boolean | null {
  if (
    f.materialGroup === undefined ||
    !isPositiveFinite(f.nps) ||
    f.designTempC === undefined ||
    !Number.isFinite(f.designTempC)
  ) {
    return null
  }
  const nps = f.nps
  const temp = f.designTempC
  return FIG_4_5_BANDS.some(
    (b) =>
      b.materialGroup === f.materialGroup &&
      nps >= b.npsMinIn &&
      nps <= b.npsMaxIn &&
      temp >= b.designTempMinC &&
      temp <= b.designTempMaxC
  )
}

/**
 * Type-A loading gate (step 7): pressure/fill must govern the required
 * thickness AND supplemental loads must not govern (§4.2.5). These are two
 * INDEPENDENT conditions, not complements — the standard checks supplemental
 * loads "even when pressure governs".
 *
 * `pressureGovernsThickness` is no longer ASKED: absent is assumed `true`, on
 * SME guidance that FFS is not run here on components where pressure does not
 * govern. That is a FLEET assumption, not a logical identity, and it has a
 * known cost: a region whose required thickness is set by minimum structural
 * thickness, with no governing supplemental loads, resolves to A here where the
 * standard would say B1 — which grants it Part 4 L1 / Part 5 L1 eligibility.
 *
 * An explicit stored `false` is still honoured, so every classification saved
 * while the question WAS asked re-classifies to exactly the same type. That is
 * what keeps `validateFfsRegionClassification` check 1 (which re-runs this
 * function over persisted facts) passing for existing records.
 *
 * `supplementalLoadsGovern` is likewise no longer ASKED: absent is assumed
 * `false`, on SME guidance that answering it correctly would require a chain
 * of calculations not relevant at this stage, and the fleet assumption is that
 * supplemental loads do not govern. That is again a FLEET assumption, not a
 * logical identity — a region whose supplemental loads actually DO govern
 * resolves to A here where the standard would say B1. An explicit stored
 * `true` is still honoured, so every classification saved while the question
 * WAS asked re-classifies to exactly the same type, keeping
 * `validateFfsRegionClassification` check 1 green for existing records.
 */
function loadsGate(f: ClassificationFacts): ClassificationResult {
  if (f.pressureGovernsThickness === false) {
    return resolved('B1', ['loads:pressure-not-governing'])
  }
  return f.supplementalLoadsGovern === true
    ? resolved('B1', ['loads:supplemental-govern'])
    : resolved('A', ['loads:pressure-governs'])
}

/**
 * Generic fallback (step 8) for an unlisted region / other-code-designed body.
 * Short-circuits: has a code design equation → the loads gate (A/B1); else a
 * code assessment procedure → B2, otherwise C.
 */
function genericFallback(f: ClassificationFacts): ClassificationResult {
  if (f.hasCodeDesignEquation === undefined)
    return moreData('hasCodeDesignEquation')
  if (f.hasCodeDesignEquation) return loadsGate(f)
  if (f.codeAssessmentProcedureAvailable === undefined) {
    return moreData('codeAssessmentProcedureAvailable')
  }
  return f.codeAssessmentProcedureAvailable
    ? resolved('B2', ['generic:procedure-available'])
    : resolved('C', ['generic:no-procedure'])
}

const B2_REGIONS: ReadonlySet<RegionKind> = new Set([
  'nozzle',
  'branch',
  'flange',
  'tubesheet',
  'cone-reinforcement',
  'cyl-to-flat-head-jct',
])
const C_REGIONS: ReadonlySet<RegionKind> = new Set([
  'skirt',
  'lug',
  'support-attach',
  'tank-shell-to-bottom-jct',
])

/**
 * Rule 2 — classify the assessment region's API 579 component type. Ordered
 * precedence (first match wins); returns `more-data` listing the missing facts
 * the moment a needed one is absent. Never defaults to A.
 */
export function classifyComponentType(
  f: ClassificationFacts
): ClassificationResult {
  // 1. Region is the highest-precedence, always-required discriminator.
  if (f.region === undefined) return moreData('region')

  // 2. Explicit Type B Class 2 discontinuities (§4.2.5).
  if (B2_REGIONS.has(f.region)) return resolved('B2', [`region:${f.region}`])

  // 3. Head-to-shell junction: 2:1-elliptical cylinder-side pressure-only => A.
  if (f.region === 'head-to-shell-jct') {
    if (f.headIs2to1Elliptical === undefined)
      return moreData('headIs2to1Elliptical')
    if (!f.headIs2to1Elliptical) return resolved('C', ['head-jct:not-2to1'])
    if (f.onCylinderSide === undefined) return moreData('onCylinderSide')
    if (!f.onCylinderSide) return resolved('C', ['head-jct:not-cylinder-side'])
    if (f.pressureOnlyLoading === undefined)
      return moreData('pressureOnlyLoading')
    return f.pressureOnlyLoading
      ? resolved('A', ['head-jct:2to1-exception'])
      : resolved('C', ['head-jct:not-pressure-only'])
  }

  // 4. Explicit Type C discontinuities.
  if (C_REGIONS.has(f.region)) return resolved('C', [`region:${f.region}`])

  // 5. Stiffening-ring attachment: Fig 4.18 ratio > 0.38 => C, else shell gates.
  if (f.region === 'stiffening-ring-attach') {
    if (!isPositiveFinite(f.stiffenerRatio)) return moreData('stiffenerRatio')
    if (f.stiffenerRatio > STIFFENER_RATIO_MAX) {
      return resolved('C', ['stiffener:ratio-over-0.38'])
    }
    // fall through to the shell gates below.
  }

  // 8 (generic fallback) — an unlisted region.
  if (f.region === 'other') return genericFallback(f)

  // 6. Body shell region (remote-shell-body or stiffener fall-through).
  if (f.shellGeometry === undefined) return moreData('shellGeometry')
  switch (f.shellGeometry) {
    case 'horizontal': {
      if (isPositiveFinite(f.D) && f.D > HORIZONTAL_D_MAX_M) {
        return resolved('B1', ['horiz:D-over-limit'])
      }
      const { Lss, D } = f
      if (!isPositiveFinite(Lss) || !isPositiveFinite(D)) {
        return moreData(...missingNumericKeys(f, ['Lss', 'D']))
      }
      return Lss / D <= HORIZONTAL_LSS_OVER_D_MAX && D <= HORIZONTAL_D_MAX_M
        ? loadsGate(f)
        : resolved('B1', ['horiz:over-limit'])
    }
    case 'vertical': {
      if (isPositiveFinite(f.H) && f.H > VERTICAL_H_MAX_M) {
        return resolved('B1', ['vert:H-over-limit'])
      }
      const { H, D } = f
      if (!isPositiveFinite(H) || !isPositiveFinite(D)) {
        return moreData(...missingNumericKeys(f, ['H', 'D']))
      }
      return H / D <= VERTICAL_H_OVER_D_MAX && H <= VERTICAL_H_MAX_M
        ? loadsGate(f)
        : resolved('B1', ['vert:over-limit'])
    }
    case 'sphere': {
      if (f.hasCodeDesignEquation === undefined)
        return moreData('hasCodeDesignEquation')
      return f.hasCodeDesignEquation ? loadsGate(f) : genericFallback(f)
    }
    case 'piping': {
      const within = isWithinFig45Bands(f)
      if (within === null) {
        return moreData('materialGroup', 'nps', 'designTempC')
      }
      return within ? loadsGate(f) : resolved('B1', ['piping:outside-fig-4.5'])
    }
    case 'other-code-designed-body':
      return genericFallback(f)
  }
}

/**
 * Per-model fact adapter: prefills ONLY the facts we can trust from the asset
 * model. It NEVER prefills `region` (geometry can't establish "remote from a
 * discontinuity"), and treats `getComponentRadius` output as OUTER diameter.
 */
export function extractClassificationFacts(
  model: GeckoComponentModel | undefined
): Partial<ClassificationFacts> {
  if (!model) return {}
  const facts: Partial<ClassificationFacts> = {}

  // Outer diameter (meters) where the radius is derivable.
  // TODO(ffs-preplanning-inside-diameter): the Fig 4.3/4.4 limits are on the
  // inside diameter; this is the OUTER diameter and needs an approved
  // conversion before it can be treated as the API D.
  const radius = getComponentRadius(model)
  const outerDm = radius?.convertTo(GeckoMeasurementUnit.Meters).num
  if (outerDm !== undefined && Number.isFinite(outerDm) && outerDm > 0) {
    facts.D = outerDm * 2
  }

  // Design temperature (°C), for the Fig 4.5 piping gate. `designTemp` is
  // only carried by `GeckoPipeSection` in the asset model today — narrowed
  // explicitly (not read off the wider piping union above) so a future piping
  // component without the field can't silently read `undefined` as 0. A
  // negative temperature is a legitimate design value, so only finiteness is
  // checked here, unlike the `D`/`Lss`/`H` dimensional facts.
  if (model.type === GeckoComponentModelType.PipeSection) {
    const designTempC = model.designTemp?.convertTo(
      GeckoMeasurementUnit.Celsius
    ).num
    if (designTempC !== undefined && Number.isFinite(designTempC)) {
      facts.designTempC = designTempC
    }
  }

  switch (model.type) {
    case GeckoComponentModelType.HorizontalTankShell:
      facts.shellGeometry = 'horizontal'
      break
    case GeckoComponentModelType.VerticalTankShell:
      facts.shellGeometry = 'vertical'
      break
    case GeckoComponentModelType.HemisphericalHead:
    case GeckoComponentModelType.Dome:
    case GeckoComponentModelType.DomeWall:
      facts.shellGeometry = 'sphere'
      break
    case GeckoComponentModelType.PipeSection:
    case GeckoComponentModelType.PipeElbow:
    case GeckoComponentModelType.PipeTJoint:
    case GeckoComponentModelType.PipeEccentricReducer: {
      facts.shellGeometry = 'piping'
      // Fig 4.5 material group (§4.2.5 piping gate), mapped from the model's
      // free-text `material`. Only ever set when the mapper is CONFIDENT — a
      // wrong family flips which Fig 4.5 band is checked, so an ambiguous or
      // unrecognised string is left unset for the engineer to pick.
      if (model.material !== undefined) {
        const materialGroup = pipingMaterialFamily(model.material)
        if (materialGroup !== undefined) {
          facts.materialGroup = materialGroup
        }
      }
      break
    }
    case GeckoComponentModelType.Cone:
    case GeckoComponentModelType.LegacyCone:
    case GeckoComponentModelType.TorisphericalHead:
    case GeckoComponentModelType.EllipsoidalHead:
      facts.shellGeometry = 'other-code-designed-body'
      break
    case GeckoComponentModelType.PressureVesselShell: {
      // Matched EXPLICITLY rather than with a ternary: the model parser casts
      // raw JSON, so an absent/corrupt/future orientation must leave the fact
      // unset for the engineer instead of silently defaulting to horizontal.
      const { orientation } = model as GeckoPressureVesselShell
      if (orientation === PressureVesselShellOrientation.Vertical) {
        facts.shellGeometry = 'vertical'
      } else if (orientation === PressureVesselShellOrientation.Horizontal) {
        facts.shellGeometry = 'horizontal'
      }
      break
    }
    default:
      // Every other body (ship / boiler / hood / floor): leave `shellGeometry`
      // unset so the engineer selects it. Adding more mappings is mechanical
      // but each one needs domain sign-off.
      break
  }

  return facts
}

/**
 * The opening answers for an LTA that has never been classified.
 *
 * On top of the model-derived facts, this defaults `region` to
 * `'remote-shell-body'` — because the inspection data visualization only places
 * indications on shell bodies, so none of the other thirteen region kinds
 * describe geometry an LTA can currently sit on.
 *
 * NOT derived from the LTA's rulers, deliberately. They measure distance to any
 * modelled asset feature — welds and plate boundaries included — and carry no
 * feature type, so they cannot establish remoteness from a MAJOR structural
 * discontinuity. They are used only to WARN against this default; see
 * `describeDiscontinuityProximity`.
 *
 * The cost of defaulting: `'remote-shell-body'` is the one region that opens the
 * Fig 4.3/4.4/4.5 shell gates, so it can reach Type A. That is why the proximity
 * warning is rendered prominently rather than as a caption.
 */
export function seedClassificationFacts(
  model: GeckoComponentModel | undefined
): ClassificationFacts {
  return { ...extractClassificationFacts(model), region: 'remote-shell-body' }
}
